import type jsPDF from "jspdf";
import type { PdfTemplateDesign } from "@workspace/api-zod/pdf-template";
import antonUrl from "../assets/fonts/Anton-Regular.ttf?url";
import poppinsUrl from "../assets/fonts/Poppins-SemiBold.ttf?url";

export const TITLE_FONTS = [
  { value: "helvetica", label: "Classic", detail: "Helvetica", family: "helvetica", css: "Arial, Helvetica, sans-serif" },
  { value: "poppins", label: "Modern", detail: "Poppins", family: "PdfTitlePoppins", css: "'PdfTitlePoppins', sans-serif" },
  { value: "anton", label: "Bold Condensed", detail: "Anton", family: "PdfTitleAnton", css: "'PdfTitleAnton', sans-serif" },
] as const;

type CustomFont = "poppins" | "anton";
const fontUrls: Record<CustomFont, string> = { anton: antonUrl, poppins: poppinsUrl };
const files = new Map<CustomFont, Promise<string>>();

export function titleFontFamily(design: PdfTemplateDesign) {
  return TITLE_FONTS.find(font => font.value === design.titleFont)?.family ?? "helvetica";
}

async function fontFile(font: CustomFont) {
  let pending = files.get(font);
  if (!pending) {
    pending = (async () => {
      const response = await fetch(fontUrls[font]);
      if (!response.ok) throw new Error("Could not load the selected title font. Please refresh and try again.");
      const bytes = new Uint8Array(await response.arrayBuffer());
      let binary = "";
      for (let i = 0; i < bytes.length; i += 8192) binary += String.fromCharCode(...bytes.subarray(i, i + 8192));
      return btoa(binary);
    })();
    files.set(font, pending);
    void pending.catch(() => { files.delete(font); });
  }
  return pending;
}

/** Only opt-in titles embed a font. Old/default documents retain their original bytes. */
export async function preparePdfTitleFont(doc: jsPDF, design: PdfTemplateDesign | null | undefined) {
  if (!design || (!design.coverEnabled && !design.thankYouEnabled) ||
    !design.pageVersion || !design.titleFont || design.titleFont === "helvetica") return;
  const family = titleFontFamily(design);
  doc.addFileToVFS(`${family}.ttf`, await fontFile(design.titleFont));
  doc.addFont(`${family}.ttf`, family, "bold");
  const previous = doc.getFont();
  doc.setFont(family, "bold");
  const registered = doc.getFont();
  doc.setFont(previous.fontName, previous.fontStyle);
  if (registered.fontName !== family ||
    !(registered.metadata as Record<string, unknown>).Unicode) {
    throw new Error("The selected title font could not be embedded. Please refresh and try again.");
  }
}
